import React from 'react';
import {
  SIMPLE_INOUT_QUICK_STATUS_OPTIONS,
  formatSimpleInOutEpoch,
  formatSimpleInOutEpochShort,
  formatSimpleInOutIso,
  simpleInOutLookupMessage,
  simpleInOutStatusLabel,
  useMySimpleInOutStatus,
} from './useMySimpleInOutStatus';
import { openSimpleInOutDotboard, openSimpleInOutStatus } from './simpleInOutEvents';

function statusButtonClassName(active: boolean, extraClass = ''): string {
  return [
    'btn',
    'xs',
    'simple-inout-status-button',
    extraClass,
    active ? 'primary' : 'btn-outline',
  ].filter(Boolean).join(' ');
}

/**
 * The Home status card. Phones show it as one card (HomeStatusControls.css);
 * on desktop Home shows SimpleInOutDotToggle instead and the full controls
 * live in the account menu. The My status modal and the dotboard drawer are
 * mounted once in AppShell (SimpleInOutOverlays).
 */
export default function SimpleInOutStatusCard() {
  const sio = useMySimpleInOutStatus();
  if (!sio.visible) return null;

  const { payload, currentStatus, updatedAt, unavailable, loading, loaded } = sio;
  const updatedLabel = loading
    ? 'Loading...'
    : unavailable
      ? simpleInOutLookupMessage(payload, sio.error)
      : payload?.stale
        ? `Cached ${formatSimpleInOutIso(payload.cached_at)}`
        : formatSimpleInOutEpoch(updatedAt);
  // Phones show a compact stamp beside the title; other states keep the full text.
  const updatedShortLabel = loaded && !payload?.stale
    ? `Updated ${formatSimpleInOutEpochShort(updatedAt)}`
    : updatedLabel;
  const summaryLabel = loading
    ? 'Loading...'
    : unavailable
      ? 'Unavailable'
      : simpleInOutStatusLabel(currentStatus);

  return (
    <div
      className="simple-inout-status-compact"
      data-state={unavailable ? 'unavailable' : payload?.stale ? 'stale' : 'ready'}
      aria-label="Simple I/O status"
    >
      <button
        type="button"
        className="simple-inout-status-compact__summary"
        onClick={openSimpleInOutStatus}
        disabled={!loaded}
        aria-label="Open Simple I/O status details"
      >
        <span className="simple-inout-status-compact__summary-main">
          <span className="simple-inout-status-compact__label">Simple I/O</span>
          <span className="simple-inout-status-pill" data-status={currentStatus || 'unknown'}>
            {summaryLabel}
          </span>
        </span>
      </button>
      <div className="simple-inout-status-compact__controls" role="group" aria-label="Quick Simple I/O status controls">
        {SIMPLE_INOUT_QUICK_STATUS_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={statusButtonClassName(currentStatus === option.value, 'simple-inout-status-button--quick')}
            data-status={option.value}
            data-active={currentStatus === option.value ? 'true' : undefined}
            aria-pressed={currentStatus === option.value}
            disabled={sio.pending || loading || !loaded}
            onClick={() => sio.setStatus(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="btn xs btn-secondary simple-inout-dotboard-button"
        disabled={loading && !unavailable}
        onClick={openSimpleInOutDotboard}
        aria-haspopup="dialog"
      >
        <svg className="simple-inout-dotboard-button__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" />
        </svg>
        <span className="simple-inout-dotboard-button__label">View dotboard</span>
      </button>
      <span className="simple-inout-status-compact__updated">
        <span className="simple-inout-status-compact__updated-full">Last updated {updatedLabel}</span>
        <span className="simple-inout-status-compact__updated-short">{updatedShortLabel}</span>
      </span>
    </div>
  );
}
